import axios from 'axios';
// Se reutiliza el cliente de utils/supabaseClient en vez de crear otro. Habia
// dos createClient apuntando a la misma clave de almacenamiento y Supabase
// avisaba por consola: dos instancias compitiendo por la sesion pueden dejar
// el token en un estado indefinido. Este modulo solo necesita leerla.
import supabase from '../utils/supabaseClient';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8787';

/**
 * Convierte una URL relativa devuelta por la API en una absoluta.
 *
 * El backend devuelve rutas como "/api/r2/objeto/<key>" porque no conoce su
 * propio dominio publico. Como el front vive en Pages y la API en Workers, hay
 * que prefijarlas. Las URLs que ya son absolutas se devuelven tal cual.
 */
export const resolverUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${apiUrl}${url}`;
};

/**
 * Obtiene el proveedor de almacenamiento activo (siempre R2)
 */
export const getStorageProvider = async () => 'r2';

/**
 * Genera una URL de miniatura.
 *
 * Antes esto tenia una rama para cdn.gallerygg.site, el dominio que dejo de
 * pagarse. Hoy todo se sirve por la API desde R2, asi que no hay transformacion
 * que aplicar: se devuelve la URL resuelta. Si algun dia se activa Cloudflare
 * Images, la transformacion va aca.
 */
export const generateThumbnailUrl = async (originalUrl) => resolverUrl(originalUrl);

/**
 * Obtiene el token de autenticación actual
 */
const getAuthToken = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || '';
  } catch (error) {
    console.error('Error al obtener el token de autenticación:', error);
    return '';
  }
};

/**
 * Sube un archivo a R2 y devuelve { key, url, bytes }.
 *
 * El archivo viaja como cuerpo crudo de un PUT, no como multipart. Es lo que
 * permite que el backend se lo pase a R2 como stream: multipart obligaria a
 * bufferizarlo entero, y un Worker tiene 128MB de memoria contra videos de
 * hasta 100MB.
 */
export const subirArchivo = async (file, profileId, { carpeta = '', onProgress } = {}) => {
  const authToken = await getAuthToken();

  const params = new URLSearchParams({
    profile_id: profileId || 'default',
    filename: file.name,
  });
  if (carpeta) params.set('carpeta', carpeta);

  const { data } = await axios.put(`${apiUrl}/api/r2/upload?${params}`, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      Authorization: `Bearer ${authToken}`,
    },
    onUploadProgress: onProgress,
  });

  return data;
};

/**
 * Sube una imagen y registra sus metadatos.
 *
 * Son dos pasos a proposito: primero el binario por streaming, despues los
 * metadatos en JSON. Si el registro falla, el archivo queda en el bucket pero
 * la subida no se da por buena.
 */
export const uploadImage = async (file, profileId, metadata = {}) => {
  const authToken = await getAuthToken();
  const subido = await subirArchivo(file, profileId);

  const { data } = await axios.post(
    `${apiUrl}/api/images`,
    { ...metadata, key: subido.key, url: subido.url, profile_id: profileId },
    { headers: { Authorization: `Bearer ${authToken}` } },
  );

  return data;
};

/**
 * Sube un video con su miniatura opcional.
 */
export const uploadVideo = async (videoFile, profileId, { thumbnail, onProgress } = {}) => {
  const authToken = await getAuthToken();

  const video = await subirArchivo(videoFile, profileId, { carpeta: 'videos', onProgress });
  const miniatura = thumbnail
    ? await subirArchivo(thumbnail, profileId, { carpeta: 'thumbnails' })
    : null;

  const { data } = await axios.post(
    `${apiUrl}/api/videos`,
    {
      key: video.key,
      url: video.url,
      thumbnail_key: miniatura?.key ?? null,
      thumbnail_url: miniatura?.url ?? null,
      title: videoFile.name,
      profile_id: profileId,
      file_size: videoFile.size,
      mime_type: videoFile.type,
    },
    { headers: { Authorization: `Bearer ${authToken}` } },
  );

  return data;
};

/**
 * Obtiene todas las imágenes.
 *
 * Antes esto pedia a dos endpoints distintos (/api/r2/images y /api/images) y
 * fusionaba los resultados, herencia de cuando convivian Supabase Storage y R2.
 * Hoy todo esta en R2 y hay un solo endpoint.
 */
export const getAllImages = async () => {
  const { data } = await axios.get(`${apiUrl}/api/images`);
  return data.map((img) => ({ ...img, url: resolverUrl(img.url) }));
};

/**
 * Obtiene las imágenes de un perfil específico
 */
export const getProfileImages = async (profileId) => {
  if (!profileId) return getAllImages();

  const { data } = await axios.get(`${apiUrl}/api/images/${profileId}`);
  return data.map((img) => ({ ...img, url: resolverUrl(img.url) }));
};

/**
 * Elimina una imagen
 */
export const deleteImage = async (imageId) => {
  const authToken = await getAuthToken();

  const { data } = await axios.delete(`${apiUrl}/api/images/${imageId}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });

  return data;
};

export default {
  resolverUrl,
  getStorageProvider,
  generateThumbnailUrl,
  subirArchivo,
  uploadImage,
  uploadVideo,
  getAllImages,
  getProfileImages,
  deleteImage,
};
